import Link from 'next/link';
import Image from 'next/image';
import { Heart, Map, Sparkles } from 'lucide-react';
import type { NavigationConfig } from '@core/ui/navigation';
import { isTenantExpUS } from '@tenants';
import type { TenantId } from 'tenants/types';
import { LocaleSwitcherContainer } from 'components/locale-switcher/exp/container';
import { buildClassName } from 'utils/build-class-name';
import type { RailDestination } from '../types';
import { RailMenu } from './rail-menu';
import styles from './left-rail.module.scss';

// The wide eXp Realty lockup needs a larger box than the compact Zoocasa wordmark
// to stay legible in the rail. Dimensions follow the SVG's 42:22 aspect ratio.
const EXP_LOGO_WIDTH = 60;
const EXP_LOGO_HEIGHT = 32;
const DEFAULT_LOGO_WIDTH = 36;
const DEFAULT_LOGO_HEIGHT = 16;

const DEFAULT_AVAILABLE_DESTINATIONS: RailDestination[] = ['map', 'collections', 'insights'];

export type LeftRailProps = {
  destination: RailDestination;
  onOpenMap: () => void;
  onOpenCollections: () => void;
  onOpenInsights: () => void;
  logoSrc: string;
  logoAlt: string;
  navConfig: NavigationConfig;
  /** Renders the enlarged eXp lockup treatment instead of the compact Zoocasa wordmark. */
  isExpLogo?: boolean;
  /** Which nav destinations to show — callers without Collections/Insights content can hide them. Defaults to all three. */
  availableDestinations?: RailDestination[];
  mapLabel?: string;
  collectionsLabel?: string;
  insightsLabel?: string;
  menuLabel?: string;
  /**
   * When provided, renders the eXp locale switcher above the hamburger when the
   * tenant has locale switching enabled (e.g. en-CA / fr-CA on eXp CA).
   */
  tenantId?: TenantId;
};

export function LeftRail({
  destination,
  onOpenMap,
  onOpenCollections,
  onOpenInsights,
  logoSrc,
  logoAlt,
  navConfig,
  isExpLogo = false,
  availableDestinations = DEFAULT_AVAILABLE_DESTINATIONS,
  mapLabel = 'Map',
  collectionsLabel = 'Collections',
  insightsLabel = 'Insights',
  menuLabel,
  tenantId,
}: LeftRailProps) {
  const isMap = destination === 'map';
  const isCollections = destination === 'collections';
  const isInsights = destination === 'insights';

  return (
    <aside data-testid="AreaPageV2-LeftRail" className={styles.root}>
      <Link
        href="/"
        className={buildClassName(styles.logoLink, isExpLogo && styles.logoLinkExp)}
        data-testid="AreaPageV2-Rail-Logo"
        aria-label={logoAlt}
      >
        <Image
          src={logoSrc}
          alt={logoAlt}
          className={buildClassName(styles.logo, isExpLogo && styles.logoExp)}
          width={isExpLogo ? EXP_LOGO_WIDTH : DEFAULT_LOGO_WIDTH}
          height={isExpLogo ? EXP_LOGO_HEIGHT : DEFAULT_LOGO_HEIGHT}
          priority
        />
      </Link>
      <nav className={styles.nav} aria-label="Area page">
        <button
          type="button"
          className={`${styles.item}${isMap ? ` ${styles.itemActive}` : ''}`}
          data-testid="AreaPageV2-Rail-Map"
          aria-current={isMap ? 'page' : undefined}
          aria-label={mapLabel}
          onClick={onOpenMap}
        >
          <Map className={styles.icon} strokeWidth={2.25} aria-hidden />
          <span className={styles.label}>{mapLabel}</span>
        </button>
        {availableDestinations.includes('collections') && (
          <button
            type="button"
            className={`${styles.item}${isCollections ? ` ${styles.itemActive}` : ''}`}
            data-testid="AreaPageV2-Rail-Collections"
            aria-current={isCollections ? 'page' : undefined}
            aria-label={collectionsLabel}
            onClick={onOpenCollections}
          >
            <Heart className={styles.icon} strokeWidth={1.9} aria-hidden />
            <span className={styles.label}>{collectionsLabel}</span>
          </button>
        )}
        {availableDestinations.includes('insights') && (
          <button
            type="button"
            className={`${styles.item}${isInsights ? ` ${styles.itemActive}` : ''}`}
            data-testid="AreaPageV2-Rail-Insights"
            aria-current={isInsights ? 'page' : undefined}
            aria-label={insightsLabel}
            onClick={onOpenInsights}
          >
            <Sparkles className={styles.icon} strokeWidth={1.9} aria-hidden />
            <span className={styles.label}>{insightsLabel}</span>
          </button>
        )}
      </nav>
      <div className={styles.footer}>
        {tenantId && !isTenantExpUS(tenantId) && (
          <div className={styles.localeSwitcher} data-testid="AreaPageV2-Rail-LocaleSwitcher">
            <LocaleSwitcherContainer tenantId={tenantId} variant="rail" />
          </div>
        )}
        <RailMenu navConfig={navConfig} menuLabel={menuLabel} />
      </div>
    </aside>
  );
}
